import type { InferRpcDefinition } from "@redotech/rpc/definition";
// TODO(tsgo): Remove when microsoft/typescript-go#1034 is fixed (check after tsgo 7.1+)
import type {} from "zod/v4/core/util";
import { adjustInventory } from "./schema/adjust-inventory";
import { exportAdjustmentHistory } from "./schema/adjustment-history/export-adjustment-history";
import { listAdjustmentHistory } from "./schema/adjustment-history/list-adjustment-history";
import { cancelAdjustmentSession } from "./schema/adjustment-session/cancel-adjustment-session";
import { createAdjustmentSession } from "./schema/adjustment-session/create-adjustment-session";
import { getAdjustmentSession } from "./schema/adjustment-session/get-adjustment-session";
import { listAdjustmentSessions } from "./schema/adjustment-session/list-adjustment-sessions";
import { submitAdjustmentSession } from "./schema/adjustment-session/submit-adjustment-session";
import { updateAdjustmentSession } from "./schema/adjustment-session/update-adjustment-session";
import { createBillOfMaterials } from "./schema/bill-of-materials/create-bill-of-materials";
import { deleteBillOfMaterials } from "./schema/bill-of-materials/delete-bill-of-materials";
import { getBillOfMaterials } from "./schema/bill-of-materials/get-bill-of-materials";
import { getBillOfMaterialsList } from "./schema/bill-of-materials/get-bill-of-materials-list";
import { getBillOfMaterialsTags } from "./schema/bill-of-materials/get-bill-of-materials-tags";
import { searchBillOfMaterials } from "./schema/bill-of-materials/search-bill-of-materials";
import { updateBillOfMaterials } from "./schema/bill-of-materials/update-bill-of-materials";
import { createAttributeDefinition } from "./schema/create-attribute-definition";
import { createInventoryTransfer } from "./schema/create-inventory-transfer";
import { deleteAttributeDefinition } from "./schema/delete-attribute-definition";
import { deleteProductFamilies } from "./schema/delete-product-families";
import { createDispute } from "./schema/disputes/create-dispute";
import { generateDisputeEmail } from "./schema/disputes/generate-dispute-email";
import { getDisputes } from "./schema/disputes/get-disputes";
import { updateDisputeStatus } from "./schema/disputes/update-dispute-status";
import { bulkUpdateForecastingFields } from "./schema/forecasting/bulk-update-forecasting-fields";
import { enrollImsBilling } from "./schema/forecasting/enroll-ims-billing";
import { getEstimatedAnnualRevenue } from "./schema/forecasting/get-estimated-annual-revenue";
import { getForecastingProducts } from "./schema/forecasting/get-forecasting-products";
import { getForecastingProductsCount } from "./schema/forecasting/get-forecasting-products-count";
import { getProductTags } from "./schema/forecasting/get-product-tags";
import { getProductVendors } from "./schema/forecasting/get-product-vendors";
import { getImsPricingInfo } from "./schema/forecasting/ims-pricing";
import { generateInventoryLevelReport } from "./schema/generate-inventory-level-report";
import { getAvailableChannels } from "./schema/get-available-channels";
import { getCatalogUniqueValues } from "./schema/get-catalog-unique-values";
import { getCatalogVendors } from "./schema/get-catalog-vendors";
import { getConsignmentCatalog } from "./schema/get-consignment-catalog";
import { getInventoryMerchants } from "./schema/get-inventory-merchants";
import { getInventoryProducts } from "./schema/get-inventory-products";
import { getInventoryProductsCount } from "./schema/get-inventory-products-count";
import { getLocations } from "./schema/get-locations";
import { getManagedReturnsMetrics } from "./schema/get-managed-returns-metrics";
import { getProductCatalog } from "./schema/get-product-catalog";
import { getProductCatalogCount } from "./schema/get-product-catalog-count";
import { getProductInventory } from "./schema/get-product-inventory";
import { getProductsForInventory } from "./schema/get-products-for-inventory";
import { getRefulfillmentMetrics } from "./schema/get-refulfillment-metrics";
import { getShopifyPublications } from "./schema/get-shopify-publications";
import { getSkuOnHandHistory } from "./schema/get-sku-on-hand-history";
import { getSkuSalesForecast } from "./schema/get-sku-sales-forecast";
import { getSkuSalesForecastBatch } from "./schema/get-sku-sales-forecast-batch";
import { getSuggestedProductsForLiquidation } from "./schema/get-suggested-products-for-liquidation";
import { getVariantInventoryByLocation } from "./schema/get-variant-inventory-by-location";
import { getWarehouses } from "./schema/get-warehouses";
import { getWarehousesForTeam } from "./schema/get-warehouses-for-team";
import { disconnectGoogleMerchantCenter } from "./schema/google-merchant-center/disconnect-google-merchant-center";
import { getGoogleMerchantCenterIntegration } from "./schema/google-merchant-center/get-google-merchant-center-integration";
import { getGoogleMerchantCenterOAuthURL } from "./schema/google-merchant-center/get-google-merchant-center-oauth-url";
import { getMerchantCenterAccounts } from "./schema/google-merchant-center/get-merchant-center-accounts";
import { getUcpManifest } from "./schema/google-merchant-center/get-ucp-manifest";
import { updateGoogleMerchantCenterIntegration } from "./schema/google-merchant-center/update-google-merchant-center-integration";
import { getHsCodes } from "./schema/hs-code-finder/get-hs-codes";
import { searchHsCodes } from "./schema/hs-code-finder/search-hs-codes";
import { listAttributeDefinitions } from "./schema/list-attribute-definitions";
import { listConsignedProducts } from "./schema/list-consigned-products";
import { streamListingFieldOptimization } from "./schema/listing-field-optimization/stream-listing-field-optimization";
import { clearListingImages } from "./schema/listing-images/clear-listing-images";
import { getAvailableListingImages } from "./schema/listing-images/get-available-listing-images";
import { updateListingImages } from "./schema/listing-images/update-listing-images";
import { updateVariantListingImages } from "./schema/listing-images/update-variant-listing-images";
import { disableListing } from "./schema/listing-management/disable-listing";
import { enableListing } from "./schema/listing-management/enable-listing";
import { createPricingRule } from "./schema/pricing-rules/create-pricing-rule";
import { deletePricingRule } from "./schema/pricing-rules/delete-pricing-rule";
import { getPricingRules } from "./schema/pricing-rules/get-pricing-rules";
import { updatePricingRule } from "./schema/pricing-rules/update-pricing-rule";
import { createProductFamily } from "./schema/product-catalog/create-product-family";
import { getProductDetails } from "./schema/product-catalog/get-product-details";
import { getProductFamilyDetails } from "./schema/product-catalog/get-product-family-details";
import { updateProduct } from "./schema/product-catalog/update-product";
import { updateProductFamily } from "./schema/product-catalog/update-product-family";
import { addProductVariant } from "./schema/product-families/add-product-variant";
import { getImageOptimizationStatus } from "./schema/product-families/get-image-optimization-status";
import { startImageOptimization } from "./schema/product-families/start-image-optimization";
import { submitImageFeedback } from "./schema/product-families/submit-image-feedback";
import { updateImageDefaults } from "./schema/product-families/update-image-defaults";
import { uploadProductFamilyImages } from "./schema/product-families/upload-product-family-images";
import { getProductTaxonomy } from "./schema/product-taxonomy-finder/get-product-taxonomy";
import { searchProductTaxonomy } from "./schema/product-taxonomy-finder/search-product-taxonomy";
import { publishProductFamilies } from "./schema/publish-product-families";
import { addItemsToPurchaseOrder } from "./schema/purchase-orders/add-items-to-purchase-order";
import { addPurchaseOrderFile } from "./schema/purchase-orders/add-purchase-order-file";
import { createPurchaseOrder } from "./schema/purchase-orders/create-purchase-order";
import { createPurchaseOrderShipment } from "./schema/purchase-orders/create-purchase-order-shipment";
import { deletePurchaseOrder } from "./schema/purchase-orders/delete-purchase-order";
import { deletePurchaseOrderFile } from "./schema/purchase-orders/delete-purchase-order-file";
import { generatePurchaseOrderPdf } from "./schema/purchase-orders/generate-purchase-order-pdf";
import { getPurchaseOrder } from "./schema/purchase-orders/get-purchase-order";
import { getPurchaseOrders } from "./schema/purchase-orders/get-purchase-orders";
import { getPurchaseOrdersByIds } from "./schema/purchase-orders/get-purchase-orders-by-ids";
import { updatePurchaseOrder } from "./schema/purchase-orders/update-purchase-order";
import { updateShipmentProducts } from "./schema/purchase-orders/update-shipment-products";
import { searchProducts } from "./schema/search-products";
import { searchProductsByBarcode } from "./schema/search-products-by-barcode";
import { addShipmentFile } from "./schema/shipments/add-shipment-file";
import { createShipment } from "./schema/shipments/create-shipment";
import { deleteShipment } from "./schema/shipments/delete-shipment";
import { deleteShipmentFile } from "./schema/shipments/delete-shipment-file";
import { getShipment } from "./schema/shipments/get-shipment";
import { getShipments } from "./schema/shipments/get-shipments";
import { linkShipmentToPurchaseOrder } from "./schema/shipments/link-shipment-to-purchase-order";
import { updateShipment } from "./schema/shipments/update-shipment";
import { addProductsToSupplier } from "./schema/supplier-products/add-products-to-supplier";
import { getProductSuppliers } from "./schema/supplier-products/get-product-suppliers";
import { validateProductsForSupplier } from "./schema/supplier-products/validate-products-for-supplier";
import { batchCreateSuppliers } from "./schema/suppliers/batch-create-suppliers";
import { createSupplier } from "./schema/suppliers/create-supplier";
import { getSupplier } from "./schema/suppliers/get-supplier";
import { getSuppliers } from "./schema/suppliers/get-suppliers";
import { mapSupplierColumns } from "./schema/suppliers/map-supplier-columns";
import { parseAddresses } from "./schema/suppliers/parse-addresses";
import { updateSupplier } from "./schema/suppliers/update-supplier";
import { addTransferFile } from "./schema/transfers/add-transfer-file";
import { createTransfer } from "./schema/transfers/create-transfer";
import { deleteTransfer } from "./schema/transfers/delete-transfer";
import { deleteTransferFile } from "./schema/transfers/delete-transfer-file";
import { getTransfer } from "./schema/transfers/get-transfer";
import { getTransfers } from "./schema/transfers/get-transfers";
import { submitTransferOrder } from "./schema/transfers/submit-transfer-order";
import { updateTransfer } from "./schema/transfers/update-transfer";
import { unpublishProductFamilies } from "./schema/unpublish-product-families";
import { updateAttributeDefinition } from "./schema/update-attribute-definition";
import { upsertInventoryItem } from "./schema/upsert-inventory-item";
import { createInventoryViewFolder } from "./schema/views/create-inventory-view-folder";
import { deleteInventoryView } from "./schema/views/delete-inventory-view";
import { deleteInventoryViewFolder } from "./schema/views/delete-inventory-view-folder";
import { getInventoryViewsAndFolders } from "./schema/views/get-inventory-views-and-folders";
import { saveInventoryView } from "./schema/views/save-inventory-view";
import { updateInventoryViewFolder } from "./schema/views/update-inventory-view-folder";
import { updateInventoryViewOrdering } from "./schema/views/update-inventory-view-ordering";

export const rpcDefinition = {
  addProductVariant,
  addPurchaseOrderFile,
  addShipmentFile,
  adjustInventory,
  batchCreateSuppliers,
  createAttributeDefinition,
  createBillOfMaterials,
  createProductFamily,
  bulkUpdateForecastingFields,
  deleteBillOfMaterials,
  disconnectGoogleMerchantCenter,
  enrollImsBilling,
  getEstimatedAnnualRevenue,
  getGoogleMerchantCenterIntegration,
  getGoogleMerchantCenterOAuthURL,
  getImsPricingInfo,
  getMerchantCenterAccounts,
  getUcpManifest,
  cancelAdjustmentSession,
  clearListingImages,
  createAdjustmentSession,
  createInventoryTransfer,
  createPricingRule,
  createPurchaseOrder,
  addItemsToPurchaseOrder,
  addProductsToSupplier,
  addTransferFile,
  createPurchaseOrderShipment,
  createInventoryViewFolder,
  createShipment,
  createDispute,
  createSupplier,
  createTransfer,
  submitTransferOrder,
  deleteAttributeDefinition,
  deletePricingRule,
  deletePurchaseOrder,
  deletePurchaseOrderFile,
  deleteInventoryView,
  deleteInventoryViewFolder,
  deleteShipment,
  deleteShipmentFile,
  deleteTransfer,
  deleteTransferFile,
  generateDisputeEmail,
  generateInventoryLevelReport,
  generatePurchaseOrderPdf,
  deleteProductFamilies,
  disableListing,
  enableListing,
  getAdjustmentSession,
  getAvailableChannels,
  getAvailableListingImages,
  getBillOfMaterials,
  getBillOfMaterialsList,
  getBillOfMaterialsTags,
  getCatalogUniqueValues,
  getCatalogVendors,
  getConsignmentCatalog,
  getDisputes,
  getForecastingProducts,
  getForecastingProductsCount,
  getHsCodes,
  getImageOptimizationStatus,
  getInventoryProducts,
  getInventoryProductsCount,
  getLocations,
  getManagedReturnsMetrics,
  getRefulfillmentMetrics,
  getPricingRules,
  getProductCatalog,
  getProductCatalogCount,
  getProductFamilyDetails,
  getProductInventory,
  getInventoryMerchants,
  getProductTags,
  getProductVendors,
  getProductsForInventory,
  getPurchaseOrder,
  getPurchaseOrdersByIds,
  getInventoryViewsAndFolders,
  getProductDetails,
  getProductSuppliers,
  getPurchaseOrders,
  getShipment,
  getShipments,
  getShopifyPublications,
  getProductTaxonomy,
  linkShipmentToPurchaseOrder,
  saveInventoryView,
  getSkuOnHandHistory,
  getSkuSalesForecast,
  getSkuSalesForecastBatch,
  getSuggestedProductsForLiquidation,
  getSupplier,
  getSuppliers,
  getTransfer,
  getTransfers,
  getVariantInventoryByLocation,
  getWarehouses,
  getWarehousesForTeam,
  listAdjustmentHistory,
  listAdjustmentSessions,
  listAttributeDefinitions,
  listConsignedProducts,
  mapSupplierColumns,
  exportAdjustmentHistory,
  parseAddresses,
  publishProductFamilies,
  searchHsCodes,
  searchBillOfMaterials,
  searchProducts,
  searchProductsByBarcode,
  searchProductTaxonomy,
  startImageOptimization,
  streamListingFieldOptimization,
  submitAdjustmentSession,
  submitImageFeedback,
  updateImageDefaults,
  updateListingImages,
  unpublishProductFamilies,
  updateAdjustmentSession,
  updateAttributeDefinition,
  updateBillOfMaterials,
  updateDisputeStatus,
  updatePricingRule,
  updateProduct,
  updateProductFamily,
  updateInventoryViewFolder,
  updateInventoryViewOrdering,
  updatePurchaseOrder,
  updateShipment,
  updateShipmentProducts,
  updateSupplier,
  updateTransfer,
  updateGoogleMerchantCenterIntegration,
  updateVariantListingImages,
  uploadProductFamilyImages,
  upsertInventoryItem,
  validateProductsForSupplier,
};

export type RedoInventoryRpcDefinition = InferRpcDefinition<
  typeof rpcDefinition
>;
